import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import ReactTable from 'react-table'
import moment from 'moment'

import { startDateFormatString } from '../../js/constants'
import './CoordinatorVisitsList.scss'
import 'react-table/react-table.css'
import { SHIP_TYPES_TEXT } from '../terminalVisitForm/shipselector/ShipSelector'
import { createVisitOrderPairs } from '../../utils/terminalVisitUtils'

export default class CoordinatorVisitsList extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      showStatusFilter: false,
      showShipTypeFilter: false
    }

    // Used to get currently shown terminal visits
    this.tableInstance = null
    this.selectedCheckboxes = new Set(props.statuses)
    this.selectedCheckboxes.delete('sailed')

    this.selectedCheckboxesShipType = new Set(Object.keys(SHIP_TYPES_TEXT))
  }

  static propTypes = {
    terminalVisits: PropTypes.array,
    statuses: PropTypes.array.isRequired,
    scrollBarWidth: PropTypes.number,
    openShipDetailsModal: PropTypes.func.isRequired,
    cargoOperationDetails: PropTypes.array.isRequired
  }

  toggleCheckbox = label => {
    if (this.selectedCheckboxes.has(label)) {
      this.selectedCheckboxes.delete(label)
    } else {
      this.selectedCheckboxes.add(label)
    }
  }

  toggleCheckboxShipType = label => {
    if (this.selectedCheckboxesShipType.has(label)) {
      this.selectedCheckboxesShipType.delete(label)
    } else {
      this.selectedCheckboxesShipType.add(label)
    }
  }

  render() {
    const columns = [
      {
        id: 'ship',
        Header: 'Ship',
        accessor: item => item.visit.ship,
        sortable: false,
        Cell: rowInfo => {
          const shipDetails = rowInfo.original.visit.ship ? rowInfo.original.visit.ship : { length: 'UNKNOWN' }
          return (
            <div>
              <span>{shipDetails.name ? shipDetails.name : shipDetails.length + ' m'}</span>
            </div>
          )
        }
      },
      {
        id: 'phonenumber',
        Header: 'Phone',
        width: 80,
        Cell: rowInfo => {
          const cargoDetails = this.props.cargoOperationDetails.find(
            operationDetails => operationDetails && operationDetails.orderId === rowInfo.original.order.uuid
          )

          if (cargoDetails) {
            return cargoDetails.phoneNumber || '-'
          } else {
            return '-'
          }
        }
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: item => item.visit.status,
        width: 80,
        Cell: rowInfo => rowInfo.value.charAt(0).toUpperCase() + rowInfo.value.slice(1),
        sortMethod: (a, b) => {
          return this.props.statuses.indexOf(a) - this.props.statuses.indexOf(b)
        }
      },
      {
        id: 'startTime',
        Header: 'Start time',
        accessor: item => item.order.cargoStart,
        Cell: rowInfo => moment(rowInfo.value).format(startDateFormatString),
        width: 120
      },
      {
        id: 'arrivalDelay',
        Header: 'Delay',
        accessor: item => item.visit.ataTime || item.visit.etaTime,
        Cell: rowInfo => {
          if (!rowInfo.value || !rowInfo.original.order.cargoStart) {
            return ''
          }

          const timeDifference = getTimeDifferenceFormatted(rowInfo.value, rowInfo.original.order.cargoStart)
          const bigDifference = timeDifference.milliseconds > 3600000 || timeDifference.milliseconds < -3600000 // 1 hour

          return <div className={bigDifference ? 'red' : ''}>{timeDifference.formatted}</div>
        },
        width: 90
      },
      {
        id: 'arrival',
        Header: 'ETA/ATA',
        accessor: item => item.visit.ataTime || item.visit.etaTime,
        Cell: rowInfo => (rowInfo.value ? moment(rowInfo.value).format(startDateFormatString) : ''),
        width: 120
      },
      {
        id: 'endTime',
        Header: 'End time',
        accessor: item => item.order.cargoEnd,
        Cell: rowInfo => (rowInfo.value ? moment(rowInfo.value).format(startDateFormatString) : ''),
        width: 120
      },
      {
        id: 'leavingDelay',
        Header: 'Delay',
        accessor: item => item.visit.atdTime || item.visit.etdTime,
        Cell: rowInfo => {
          if (!rowInfo.value || !rowInfo.original.order.cargoEnd) {
            return ''
          }
          const timeDifference = getTimeDifferenceFormatted(rowInfo.value, rowInfo.original.order.cargoEnd)
          const bigDifference = timeDifference.milliseconds > 3600000 || timeDifference.milliseconds < -3600000 // 1 hour

          return <div className={bigDifference ? 'red' : ''}>{timeDifference.formatted}</div>
        },
        width: 90
      },
      {
        id: 'leaving',
        Header: 'ETD/ATD',
        accessor: item => item.visit.etdTime || item.visit.atdTime,
        Cell: rowInfo => (rowInfo.value ? moment(rowInfo.value).format(startDateFormatString) : '-'),
        width: 120
      },
      {
        id: 'product',
        Header: 'Product',
        accessor: item => item.order.product,
        Cell: rowInfo => {
          return (
            <div>
              <span
                className={`fa ${
                  rowInfo.original.order.operation === 'unloading' ? 'fa-arrow-circle-up' : 'fa-arrow-circle-down'
                } cargo-type-indicator`}
                style={{ color: rowInfo.original.order.color ? rowInfo.original.order.color : '#000' }}
              />
              {rowInfo.value}
            </div>
          )
        }
      },
      {
        id: 'amount',
        Header: 'Amount',
        accessor: item => item.order.amount,
        Cell: rowInfo => rowInfo.value + ' mt',
        width: 65
      },
      {
        id: 'origin',
        Header: 'Origin',
        accessor: 'order.origin',
        width: 100
      },
      {
        id: 'booklet',
        Header: 'Details',
        width: 170,
        Cell: rowInfo => {
          return (
            <div>
              <button
                className="button"
                onClick={() => {
                  this.props.openShipDetailsModal(
                    rowInfo.original.visit.ship,
                    rowInfo.original.order,
                    rowInfo.original.visit._id
                  )
                }}
              >
                Cargo Operation Details
              </button>
            </div>
          )
        }
      }
    ]

    const prettyScrollbarClass = this.props.scrollBarWidth ? 'prettyscrollbarfix' : ''

    // create order+visit pairs
    const items = createVisitOrderPairs(this.props.terminalVisits)

    return (
      <ReactTable
        ref={el => (this.tableInstance = el)}
        data={items}
        columns={columns}
        defaultFilterMethod={this.filterMethod}
        pageSize={items.length > 8 && items.length < 16 ? items.length : 12}
        showPageSizeOptions={false}
        className={'-striped -highlight coordinator-table ' + prettyScrollbarClass}
        defaultFiltered={[
          {
            id: 'status',
            value: ''
          }
        ]}
        getTdProps={(state, rowInfo, column) => {
          return {
            onClick:
              rowInfo && rowInfo.original
                ? () => {
                    if (column.id === 'booklet') {
                      return
                    }

                    this.props.openShipDetailsModal(
                      rowInfo.original.visit.ship,
                      rowInfo.original.order,
                      rowInfo.original.visit._id
                    )
                  }
                : () => ({})
          }
        }}
      />
    )
  }

  filterMethod = (filter, row) => {
    const id = filter.pivotId || filter.id
    return typeof row[id] === 'string' ? String(row[id].toLowerCase()).startsWith(filter.value.toLowerCase()) : true
  }

  handleToggleStatusFilter = () => {
    this.setState({ showStatusFilter: !this.state.showStatusFilter })
  }

  handleToggleShipTypeFilter = () => {
    this.setState({ showShipTypeFilter: !this.state.showShipTypeFilter })
  }
}

/**
 * @param {number} delayedTime
 * @param {number} orignalTime
 * @return {{milliseconds: number, formatted: string}}
 */
function getTimeDifferenceFormatted(delayedTime, orignalTime) {
  const millisecondsDiff = delayedTime - orignalTime

  const isNegative = millisecondsDiff < 0
  const positiveMs = isNegative ? millisecondsDiff * -1 : millisecondsDiff
  return { milliseconds: millisecondsDiff, formatted: (isNegative ? '-' : '') + msToTime(positiveMs) }
}

/**
 * @param {number} milliseconds
 * @return {string}
 */
function msToTime(milliseconds) {
  const singleDay = 86400000
  const singleHour = 3600000
  const singleMinute = 60000
  const days = Math.floor(milliseconds / singleDay)
  const hours = Math.floor((milliseconds - days * singleDay) / singleHour)
  const minutes = Math.floor((milliseconds - days * singleDay - hours * singleHour) / singleMinute)

  if (days > 1) {
    return days + 'd, ' + hours + 'h'
  } else {
    return hours + 'h, ' + minutes + 'm'
  }
}
